import React, { useEffect, useRef } from 'react';
import { AmityUiKitSocial, AmityPostDetailPage, useAmityNavigation } from '@frn/amityco-ui-kit-open-source';
// eslint-disable-next-line import/no-webpack-loader-syntax
import socialPlusStyles from '!!css-loader?{"sourceMap":false,"exportType":"string"}!@frn/amityco-ui-kit-open-source/dist/index.css';
import overrideStyles from '!!css-loader?{"sourceMap":false,"exportType":"string"}!sass-loader!./globalOverrides.scss';

import ErrorBoundary from '../ErrorBoundary';
import useAutoJoinSocialPlusCommunity from './useAutoJoinSocialPlusCommunity';

const SocialPlusForum = ({ style, useForum = true, postId, autoJoinCommunityIds = [] }) => {
  const { pushPage, PageTypes } = useAmityNavigation();

  const urlToPath = () => {
    let path = null;
    const urlParts = location.hash.split('/');
    if (urlParts[1] === 'post') {
      if (urlParts[2]) {
        path = {
          post: urlParts[1],
          postId: urlParts[2]
        };
      }
    }
    if (urlParts[3] && urlParts[3] === 'comment') {
      if (path && urlParts[4]) {
        path['comment'] = urlParts[3];
        path['commentId'] = urlParts[4];
      }
    }
    return path;
  };

  const wrapperRef = useRef(null);

  useEffect(() => {
    let path = urlToPath();
    if (path || postId)
      pushPage({
        type: PageTypes.PostDetailPage,
        context: {
          postId: path ? path.postId : postId,
          commentId: path ? path.commentId : null
        }
      });
  }, []);

  /* Force instant scrollIntoView for elements inside the wrapper.
   * iamityco-ui-kit-open-source dispatches in an internal overflow container
   * Firefox's smooth scroll animation continuously re-triggers layout, breaking
   * the grid. Instant scroll avoids this, and scroll-margin-top (in CSS) handles
   * the header offset correctly with behavior:'auto' in all browsers. */

  // The monkeypatch here is needed for Firefox, unnecessary for Chrome
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const originalScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function (arg) {
      if (wrapper.contains(this) && typeof arg === 'object') {
        originalScrollIntoView.call(this, { ...arg, behavior: 'auto' });
      } else {
        originalScrollIntoView.call(this, arg);
      }
    };

    return () => {
      Element.prototype.scrollIntoView = originalScrollIntoView;
    };
  }, []);

  /* The 3rd-party SDK dispatches 'comment-scroll-complete' after its scroll.
   * Chrome needs a reflow toggle to fix a grid layout bug where nested
   * height:100% elements don't recalculate after content changes.
   */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const forceReflow = () => {
      wrapper.style.paddingTop = '0px';
      void wrapper.offsetHeight;
      wrapper.style.paddingTop = '';
    };

    document.addEventListener('social-plus:comment-scroll-complete', forceReflow);
    return () => document.removeEventListener('social-plus:comment-scroll-complete', forceReflow);
  }, []);

  /* When navigating to PostDetailPage, we need a reflow to ensure padding is correct
   * for the embedded Social+ library. Use addedNodes to check when we first navigate
   * to PostDetailPage, and also use resizeObserver and a 500ms timer to ensure
   * the bounds stay where they are supposed to */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const postDetailEl =
            node.querySelector('[data-testid^="post_detail_page/"]') ||
            (node.matches('[data-testid^="post_detail_page/"]') ? node : null);

          if (postDetailEl) {
            let debounceTimer;
            const resizeObserver = new ResizeObserver(() => {
              clearTimeout(debounceTimer);
              debounceTimer = setTimeout(() => {
                resizeObserver.disconnect();
                wrapper.style.paddingTop = '0px';
                void wrapper.offsetHeight;
                wrapper.style.paddingTop = '';
              }, 500);
            });
            resizeObserver.observe(postDetailEl);

            return;
          }
        }
      }
    });
    observer.observe(wrapper, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (autoJoinCommunityIds.length > 0) useAutoJoinSocialPlusCommunity(autoJoinCommunityIds);

  return (
    <ErrorBoundary msg="Error loading the community forum, please refresh your browser.">
      <div
        ref={wrapperRef}
        className={useForum && 'socialplus-uikit-wrapper'}
        style={
          style || {
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100vw',
            height: '100dvh'
          }
        }
      >
        <style type="text/css" scoped>
          {socialPlusStyles}
        </style>
        <style>{useForum && overrideStyles}</style>
        {useForum && <AmityUiKitSocial />}
      </div>
    </ErrorBoundary>
  );
};

export default SocialPlusForum;
